import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { useSession } from './stores/session';
import { USE_MOCKS } from './api/client';
import { logout } from './api/auth';
import { useBrand } from './brand/BrandProvider';

import { AppShell } from './shell/AppShell';
import { AdminShell } from './shell/AdminShell';
import { ResellerShell } from './shell/ResellerShell';
import { StaffShell } from './shell/StaffShell';
import { CommandPalette } from './shell/CommandPalette';
import { CookieConsent } from './shell/CookieConsent';
import { ImpersonationBanner } from './components/ImpersonationBanner';

// Owner (existing)
import { Dashboard } from './pages/Dashboard';
import { Login } from './pages/Login';
import { Businesses } from './pages/Businesses';
import { Subscription } from './pages/Subscription';
import { Billing } from './pages/Billing';
import { Profile } from './pages/Profile';

// Owner extras
import { NewBusiness } from './pages/owner/NewBusiness';
import { InvoiceDetail } from './pages/owner/InvoiceDetail';
import { Team } from './pages/owner/Team';
import { AddOns } from './pages/owner/AddOns';
import { Suspended } from './pages/owner/Suspended';
import { Notifications } from './pages/Notifications';

// Auth flows
import { Signup } from './pages/auth/Signup';
import { SignupProvisioning } from './pages/auth/SignupProvisioning';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';
import { AcceptInvite } from './pages/auth/AcceptInvite';
import { Bootstrap } from './pages/auth/Bootstrap';
import { Welcome } from './pages/auth/Welcome';
import { BecomeAReseller } from './pages/auth/BecomeAReseller';
import { BecomeAResellerSubmitted } from './pages/auth/BecomeAResellerSubmitted';
import { AccountChooser } from './pages/auth/AccountChooser';
import { Help } from './pages/Help';

// Marketing
import { Pricing } from './pages/marketing/Pricing';
import { Contact } from './pages/marketing/Contact';
import { Landing } from './pages/marketing/Landing';
import { PlatformLanding } from './pages/marketing/PlatformLanding';
import { Company } from './pages/marketing/Company';
import { Features } from './pages/marketing/Features';
import { About } from './pages/marketing/About';
import { Blog, BlogPost } from './pages/marketing/Blog';
import { Privacy } from './pages/marketing/Privacy';
import { Terms } from './pages/marketing/Terms';
import { Refund } from './pages/marketing/Refund';
import { Cookies } from './pages/marketing/Cookies';
import { Cms } from './pages/admin/Cms';

// Admin
import { AdminLogin } from './pages/admin/AdminLogin';
import { AdminOverview } from './pages/admin/AdminOverview';
import { Resellers as AdminResellers } from './pages/admin/Resellers';
import { ResellerDetail } from './pages/admin/ResellerDetail';
import { Businesses as AdminBusinesses } from './pages/admin/Businesses';
import { BusinessDetail } from './pages/admin/BusinessDetail';
import { Packages as AdminPackages } from './pages/admin/Packages';
import { PackageEditor } from './pages/admin/PackageEditor';
import { Addons as AdminAddons } from './pages/admin/Addons';
import { Coupons as AdminCoupons } from './pages/admin/Coupons';
import { PackageSubscribers } from './pages/admin/PackageSubscribers';
import { Gateways } from './pages/admin/Gateways';
import { CashEntry } from './pages/admin/CashEntry';
import { Webhooks } from './pages/admin/Webhooks';
import { AuditLog } from './pages/admin/AuditLog';
import { SystemHealth } from './pages/admin/SystemHealth';
import { PlatformSettings } from './pages/admin/PlatformSettings';
import { Users as AdminUsers } from './pages/admin/Users';
import { UserDetail as AdminUserDetail } from './pages/admin/UserDetail';
import { Domains as AdminDomains } from './pages/admin/Domains';
import { Payments as AdminPayments } from './pages/admin/Payments';
import { BillingFlow } from './pages/admin/BillingFlow';
import { ResellerApplications } from './pages/admin/ResellerApplications';
import { PricingTiers } from './pages/admin/PricingTiers';
import { PackageCategories } from './pages/admin/PackageCategories';
import { Broadcasts as AdminBroadcasts } from './pages/admin/Broadcasts';

// Reseller
import { ResellerLogin } from './pages/reseller/ResellerLogin';
import { ResellerSetPassword } from './pages/reseller/ResellerSetPassword';
import { ResellerOnboarding } from './pages/reseller/ResellerOnboarding';
import { ResellerOverview } from './pages/reseller/ResellerOverview';
import { Customers as ResellerCustomers } from './pages/reseller/Customers';
import { CustomerCreate } from './pages/reseller/CustomerCreate';
import { CustomerDetail } from './pages/reseller/CustomerDetail';
import { Staff as ResellerStaffPage } from './pages/reseller/Staff';
import { Branding as ResellerBranding } from './pages/reseller/Branding';
import { Content as ResellerContent } from './pages/reseller/Content';
import { Domains as ResellerDomains } from './pages/reseller/Domains';
import { PackagesStrategy } from './pages/reseller/PackagesStrategy';
import { ResellerAddons } from './pages/reseller/Addons';
import { Coupons as ResellerCoupons } from './pages/reseller/Coupons';
import { Revenue } from './pages/reseller/Revenue';
import { Payments as ResellerPayments } from './pages/reseller/Payments';
import { ResellerSettings } from './pages/reseller/ResellerSettings';
import { ApplicationStatus as ResellerApplicationStatus } from './pages/reseller/ApplicationStatus';

// POS-side states
import { SsoExpired } from './pages/pos/SsoExpired';
import { PosSuspended } from './pages/pos/PosSuspended';
import { DbUnreachable } from './pages/pos/DbUnreachable';

// Error pages
import { NotFound } from './pages/errors/NotFound';
import { Forbidden } from './pages/errors/Forbidden';
import { Unauthorized } from './pages/errors/Unauthorized';
import { ServerError } from './pages/errors/ServerError';

// Staff
import { StaffLanding } from './pages/staff/StaffLanding';

// Billing
import { PaymentReturn } from './pages/billing/PaymentReturn';

/* ────────────────────────────────────────────────────────────────── */
/*  Route tree                                                        */
/* ────────────────────────────────────────────────────────────────── */

function RootLayout() {
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    function onCustom() {
      setPaletteOpen(true);
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('open-command-palette', onCustom as EventListener);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('open-command-palette', onCustom as EventListener);
    };
  }, []);

  return (
    <>
      <Outlet />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <CookieConsent />
      <ImpersonationBanner />
    </>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

/**
 * Public home, resolved by host brand (Decision #21). On a reseller host the
 * bare domain must show the reseller-branded Landing page (no ITCity/Possignin
 * leak); on the platform host it shows the ITCity parent Company page. The brand
 * is resolved before first paint in main.tsx's SessionGate, so this pick is
 * stable and flash-free.
 */
function HomeByHost() {
  const brand = useBrand();
  if (!brand.isPlatform) return <Landing />; // reseller host → reseller-branded landing
  // Platform: the ITCity company site (itcitysoft.com / itcity.ltd) shows the ITCity
  // parent Company page; the Possignin product site (possignin.com) shows the
  // Possignin product landing — not the company page.
  const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const itcityCompany =
    host === 'itcitysoft.com' || host.endsWith('.itcitysoft.com') ||
    host === 'itcity.ltd' || host.endsWith('.itcity.ltd');
  return itcityCompany ? <Company /> : <PlatformLanding />;
}

/**
 * Guard for platform-only marketing pages (Features / About / Blog / the Possignin
 * product landing / the become-a-reseller funnel). Their copy is ITCity/Possignin
 * marketing, so on a reseller (white-label) host we must not render them even by
 * direct URL — redirect to the reseller's own home (Decision #21). Platform hosts
 * render the page unchanged. (Legal pages stay reachable on every host — they name
 * the operating entity by design.)
 */
function PlatformOnly({ children }: { children: ReactNode }) {
  const brand = useBrand();
  if (!brand.isPlatform) return <Navigate to="/" replace />;
  return <>{children}</>;
}
const platformOnly = (Comp: ComponentType) => () => (
  <PlatformOnly>
    <Comp />
  </PlatformOnly>
);

/**
 * Route guard for the four authenticated shells. SessionGate (main.tsx) blocks
 * the whole app on bootstrapSession() before the router renders, so by the time
 * beforeLoad runs the session is authoritative — a null user means truly signed
 * out, and we send them to /login (instead of rendering a shell that 401s every
 * request). Mock mode always has a seeded session, so this is a no-op there.
 */
const requireAuth = () => {
  if (!USE_MOCKS && !useSession.getState().user) {
    throw redirect({ to: '/login' });
  }
};

/**
 * Dedicated sign-out route. The shells' "Sign out" controls point here (a plain
 * <Link to="/logout">) and this component performs the real logout on mount, then
 * lands on /login. It's a COMPONENT (not a beforeLoad) on purpose: the router
 * preloads on intent (hover), and a beforeLoad-based logout would sign the user
 * out just by hovering the menu item. A component only runs on real navigation.
 */
function Logout() {
  const navigate = useNavigate();
  useEffect(() => {
    void logout().finally(() => navigate({ to: '/login', replace: true }));
  }, [navigate]);
  return (
    <div className="min-h-screen grid place-items-center bg-white dark:bg-zinc-950 text-sm text-zinc-500 dark:text-zinc-400">
      Signing you out…
    </div>
  );
}

/* Owner shell */
const appLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app-layout',
  // Owner shell. A signed-OUT visitor landing on the owner home (`/`) is sent to
  // the PUBLIC marketing landing (`/home`, the ITCity company page) rather than a
  // login form — the bare domain should sell the product, not gate it. Every other
  // protected owner route still redirects a guest to /login. Authed owners get the
  // dashboard as before.
  beforeLoad: ({ location }) => {
    if (!USE_MOCKS && !useSession.getState().user) {
      throw redirect({ to: location.pathname === '/' ? '/home' : '/login' });
    }
  },
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const dashboardRoute = createRoute({ getParentRoute: () => appLayout, path: '/', component: Dashboard });
const businessesRoute = createRoute({ getParentRoute: () => appLayout, path: '/businesses', component: Businesses });
const newBusinessRoute = createRoute({ getParentRoute: () => appLayout, path: '/businesses/new', component: NewBusiness });
const subscriptionRoute = createRoute({ getParentRoute: () => appLayout, path: '/subscription', component: Subscription });
const billingRoute = createRoute({ getParentRoute: () => appLayout, path: '/billing', component: Billing });
const invoiceDetailRoute = createRoute({ getParentRoute: () => appLayout, path: '/billing/$id', component: InvoiceDetail });
const addOnsRoute = createRoute({ getParentRoute: () => appLayout, path: '/add-ons', component: AddOns });
const teamRoute = createRoute({ getParentRoute: () => appLayout, path: '/team', component: Team });
const profileRoute = createRoute({ getParentRoute: () => appLayout, path: '/profile', component: Profile });
const notificationsRoute = createRoute({ getParentRoute: () => appLayout, path: '/notifications', component: Notifications });
const helpRoute = createRoute({ getParentRoute: () => appLayout, path: '/help', component: Help });

/* Admin shell */
const adminLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'admin-layout',
  beforeLoad: requireAuth,
  component: () => (
    <AdminShell>
      <Outlet />
    </AdminShell>
  ),
});

const adminOverviewRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin', component: AdminOverview });
const adminResellersRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/resellers', component: AdminResellers });
const adminResellerDetailRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/resellers/$id', component: ResellerDetail });
const adminBusinessesRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/businesses', component: AdminBusinesses });
const adminBusinessNewRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/businesses/new', component: CustomerCreate });
const adminBusinessDetailRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/businesses/$id', component: BusinessDetail });
const adminUsersRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/users', component: AdminUsers });
const adminUserDetailRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/users/$id', component: AdminUserDetail });
const adminPackagesRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/packages', component: AdminPackages });
const adminPackageEditorRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/packages/$id/edit', component: PackageEditor });
const adminPackageSubscribersRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/packages/$id/subscribers', component: PackageSubscribers });
const adminAddonsRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/addons', component: AdminAddons });
const adminCouponsRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/coupons', component: AdminCoupons });
const adminPaymentsRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/payments', component: AdminPayments });
const adminBillingFlowRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/billing-flow', component: BillingFlow });
const adminCashEntryRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/cash-entry', component: CashEntry });
const adminGatewaysRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/gateways', component: Gateways });
const adminDomainsRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/domains', component: AdminDomains });
const adminWebhooksRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/webhooks', component: Webhooks });
const adminAuditRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/audit', component: AuditLog });
const adminHealthRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/health', component: SystemHealth });
const adminSettingsRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/settings', component: PlatformSettings });
const adminResellerApplicationsRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/resellers/applications', component: ResellerApplications });
const adminPricingTiersRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/pricing-tiers', component: PricingTiers });
const adminPackageCategoriesRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/package-categories', component: PackageCategories });
const adminBroadcastsRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/broadcasts', component: AdminBroadcasts });
const adminCmsRoute = createRoute({ getParentRoute: () => adminLayout, path: '/admin/cms', component: Cms });

/* Reseller shell */
const resellerLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'reseller-layout',
  beforeLoad: requireAuth,
  component: () => (
    <ResellerShell>
      <Outlet />
    </ResellerShell>
  ),
});

const resellerOverviewRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller', component: ResellerOverview });
const resellerCustomersRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/customers', component: ResellerCustomers });
const resellerCustomerCreateRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/customers/new', component: CustomerCreate });
const resellerCustomerDetailRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/customers/$id', component: CustomerDetail });
const resellerStaffRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/staff', component: ResellerStaffPage });
const resellerBrandingRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/branding', component: ResellerBranding });
const resellerContentRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/content', component: ResellerContent });
const resellerDomainsRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/domains', component: ResellerDomains });
const resellerPackagesRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/packages', component: PackagesStrategy });
const resellerAddonsRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/addons', component: ResellerAddons });
const resellerCouponsRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/coupons', component: ResellerCoupons });
const resellerRevenueRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/revenue', component: Revenue });
const resellerPaymentsRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/payments', component: ResellerPayments });
const resellerSettingsRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/settings', component: ResellerSettings });
const resellerStatusRoute = createRoute({ getParentRoute: () => resellerLayout, path: '/reseller/status', component: ResellerApplicationStatus });

/* Staff shell */
const staffLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'staff-layout',
  beforeLoad: requireAuth,
  component: () => (
    <StaffShell>
      <Outlet />
    </StaffShell>
  ),
});

const staffLandingRoute = createRoute({ getParentRoute: () => staffLayout, path: '/staff', component: StaffLanding });

/* Bare (no shell) */
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: Login });
const logoutRoute = createRoute({ getParentRoute: () => rootRoute, path: '/logout', component: Logout });
const signupRoute = createRoute({ getParentRoute: () => rootRoute, path: '/signup', component: Signup });
const signupProvisioningRoute = createRoute({ getParentRoute: () => rootRoute, path: '/signup/provisioning', component: SignupProvisioning });
const forgotPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/forgot-password', component: ForgotPassword });
const resetPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/reset-password', component: ResetPassword });
const acceptInviteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/accept-invite', component: AcceptInvite });
const bootstrapRoute = createRoute({ getParentRoute: () => rootRoute, path: '/bootstrap', component: Bootstrap });
const welcomeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/welcome', component: Welcome });
const suspendedRoute = createRoute({ getParentRoute: () => rootRoute, path: '/suspended', component: Suspended });
const paymentReturnRoute = createRoute({ getParentRoute: () => rootRoute, path: '/billing/return', component: PaymentReturn });

const pricingRoute = createRoute({ getParentRoute: () => rootRoute, path: '/pricing', component: Pricing });
const contactRoute = createRoute({ getParentRoute: () => rootRoute, path: '/contact', component: Contact });
const landingRoute = createRoute({ getParentRoute: () => rootRoute, path: '/landing', component: Landing });
const platformHomeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/home', component: HomeByHost });
const possigninRoute = createRoute({ getParentRoute: () => rootRoute, path: '/possignin', component: platformOnly(PlatformLanding) });
const featuresRoute = createRoute({ getParentRoute: () => rootRoute, path: '/features', component: platformOnly(Features) });
const aboutRoute = createRoute({ getParentRoute: () => rootRoute, path: '/about', component: platformOnly(About) });
const blogRoute = createRoute({ getParentRoute: () => rootRoute, path: '/blog', component: platformOnly(Blog) });
const blogPostRoute = createRoute({ getParentRoute: () => rootRoute, path: '/blog/$slug', component: platformOnly(BlogPost) });
const privacyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/privacy', component: Privacy });
const termsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/terms', component: Terms });
const refundRoute = createRoute({ getParentRoute: () => rootRoute, path: '/refund', component: Refund });
const cookiesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/cookies', component: Cookies });
const becomeAResellerRoute = createRoute({ getParentRoute: () => rootRoute, path: '/become-a-reseller', component: platformOnly(BecomeAReseller) });
const becomeAResellerSubmittedRoute = createRoute({ getParentRoute: () => rootRoute, path: '/become-a-reseller/submitted', component: platformOnly(BecomeAResellerSubmitted) });
const becomeAResellerStatusRoute = createRoute({ getParentRoute: () => rootRoute, path: '/become-a-reseller/status', component: platformOnly(ResellerApplicationStatus) });
const accountChooserRoute = createRoute({ getParentRoute: () => rootRoute, path: '/account-chooser', component: AccountChooser });

const adminLoginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/admin/login', component: AdminLogin });
const resellerLoginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/reseller/login', component: ResellerLogin });
const resellerSetPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/reseller/set-password', component: ResellerSetPassword });
const resellerOnboardingRoute = createRoute({ getParentRoute: () => rootRoute, path: '/reseller/onboarding', component: ResellerOnboarding });

const ssoExpiredRoute = createRoute({ getParentRoute: () => rootRoute, path: '/pos/sso-expired', component: SsoExpired });
const posSuspendedRoute = createRoute({ getParentRoute: () => rootRoute, path: '/pos/suspended', component: PosSuspended });
const dbUnreachableRoute = createRoute({ getParentRoute: () => rootRoute, path: '/pos/db-unreachable', component: DbUnreachable });

const error401Route = createRoute({ getParentRoute: () => rootRoute, path: '/errors/401', component: Unauthorized });
const error403Route = createRoute({ getParentRoute: () => rootRoute, path: '/errors/403', component: Forbidden });
const error404Route = createRoute({ getParentRoute: () => rootRoute, path: '/errors/404', component: NotFound });
const error500Route = createRoute({ getParentRoute: () => rootRoute, path: '/errors/500', component: ServerError });

/* Compose */
const routeTree = rootRoute.addChildren([
  appLayout.addChildren([
    dashboardRoute,
    businessesRoute,
    newBusinessRoute,
    subscriptionRoute,
    billingRoute,
    invoiceDetailRoute,
    addOnsRoute,
    teamRoute,
    profileRoute,
    notificationsRoute,
    helpRoute,
  ]),
  adminLayout.addChildren([
    adminOverviewRoute,
    adminResellersRoute,
    adminResellerDetailRoute,
    adminBusinessesRoute,
    adminBusinessNewRoute,
    adminBusinessDetailRoute,
    adminUsersRoute,
    adminPackagesRoute,
    adminPackageEditorRoute,
    adminPackageSubscribersRoute,
    adminAddonsRoute,
    adminCouponsRoute,
    adminPaymentsRoute,
    adminBillingFlowRoute,
    adminCashEntryRoute,
    adminGatewaysRoute,
    adminDomainsRoute,
    adminWebhooksRoute,
    adminAuditRoute,
    adminHealthRoute,
    adminSettingsRoute,
    adminResellerApplicationsRoute,
    adminPricingTiersRoute,
    adminPackageCategoriesRoute,
    adminBroadcastsRoute,
    adminCmsRoute,
    adminUserDetailRoute,
  ]),
  resellerLayout.addChildren([
    resellerOverviewRoute,
    resellerCustomersRoute,
    resellerCustomerCreateRoute,
    resellerCustomerDetailRoute,
    resellerStaffRoute,
    resellerBrandingRoute,
    resellerContentRoute,
    resellerDomainsRoute,
    resellerPackagesRoute,
  resellerAddonsRoute,
    resellerCouponsRoute,
    resellerRevenueRoute,
    resellerPaymentsRoute,
    resellerSettingsRoute,
    resellerStatusRoute,
  ]),
  staffLayout.addChildren([staffLandingRoute]),
  loginRoute,
  logoutRoute,
  signupRoute,
  signupProvisioningRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  acceptInviteRoute,
  bootstrapRoute,
  welcomeRoute,
  suspendedRoute,
  paymentReturnRoute,
  pricingRoute,
  contactRoute,
  landingRoute,
  platformHomeRoute,
  possigninRoute,
  featuresRoute,
  aboutRoute,
  blogRoute,
  blogPostRoute,
  privacyRoute,
  termsRoute,
  refundRoute,
  cookiesRoute,
  becomeAResellerRoute,
  becomeAResellerSubmittedRoute,
  becomeAResellerStatusRoute,
  accountChooserRoute,
  adminLoginRoute,
  resellerLoginRoute,
  resellerSetPasswordRoute,
  resellerOnboardingRoute,
  ssoExpiredRoute,
  posSuspendedRoute,
  dbUnreachableRoute,
  error401Route,
  error403Route,
  error404Route,
  error500Route,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
